import { Dayjs } from 'dayjs';
import { DTN_API } from 'configs/endpoints';
import { dtnTokenManager } from 'services/dtn/auth';
import { DTNTimestamp, DTNTimestampWrapper } from 'services/dtn/types';
import { flattenDTNTimestamps } from './createDTNTileSource';

const cache = new Map<string, DTNTimestamp[]>();
const sampledCache = new Map<string, DTNTimestamp[]>();

export function setCachedTimestamps(layerId: string, timestamps: DTNTimestamp[]): void {
  cache.set(layerId, timestamps);
}

export function getCachedTimestamps(layerId: string): DTNTimestamp[] | undefined {
  return cache.get(layerId);
}

// Stores only the DTN timestamps that were actually selected during preloading
// (the sampled subset). Used during playback to guarantee cache hits.
export function setSampledTimestamps(layerId: string, timestamps: DTNTimestamp[]): void {
  sampledCache.set(layerId, timestamps);
}

export function getSampledTimestamps(layerId: string): DTNTimestamp[] | undefined {
  return sampledCache.get(layerId);
}

function splitInto24hWindows(start: Dayjs, end: Dayjs): Array<{ from: string; to: string }> {
  const windows: Array<{ from: string; to: string }> = [];
  let cursor = start;
  while (cursor.isBefore(end)) {
    const windowEnd = cursor.add(24, 'hour');
    windows.push({
      from: cursor.toISOString(),
      to: (windowEnd.isAfter(end) ? end : windowEnd).toISOString(),
    });
    cursor = windowEnd;
  }
  return windows;
}

async function fetchWindow(
  layerId: string,
  from: string,
  to: string,
  token: string,
): Promise<DTNTimestamp[]> {
  // The API expects a single ISO 8601 interval: ?time=start/end
  const url = `${DTN_API}/timestamps/${layerId}?time=${encodeURIComponent(`${from}/${to}`)}`;
  const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' } });
  if (!resp.ok) return [];
  const data: DTNTimestampWrapper[] = await resp.json();
  return flattenDTNTimestamps(data);
}

export async function fetchAndCacheDTNTimestamps(
  layerId: string,
  range: [Dayjs, Dayjs],
): Promise<DTNTimestamp[]> {
  const token = await dtnTokenManager.getToken();
  const windows = splitInto24hWindows(range[0], range[1]);

  // Range is zero-length or inverted — fall back to latest available
  if (windows.length === 0) {
    // eslint-disable-next-line no-use-before-define
    return fetchLatestDTNTimestamps(layerId);
  }

  const results = await Promise.all(
    windows.map(({ from, to }) => fetchWindow(layerId, from, to, token)),
  );

  const seen = new Set<string>();
  const all = results.flat().filter((ts) => {
    if (seen.has(ts.tileSetId)) return false;
    seen.add(ts.tileSetId);
    return true;
  });
  all.sort((a, b) => new Date(a.valid).getTime() - new Date(b.valid).getTime());

  // Range produced no results — layer may have data outside the selected window.
  // Fall back to the latest available timestamps so the layer still loads.
  if (all.length === 0) {
    // eslint-disable-next-line no-use-before-define
    return fetchLatestDTNTimestamps(layerId);
  }

  setCachedTimestamps(layerId, all);
  return all;
}

export async function fetchLatestDTNTimestamps(layerId: string): Promise<DTNTimestamp[]> {
  const token = await dtnTokenManager.getToken();
  const url = `${DTN_API}/timestamps/${layerId}`;
  const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' } });
  if (!resp.ok) return [];
  const data: DTNTimestampWrapper[] = await resp.json();
  const timestamps = flattenDTNTimestamps(data);
  setCachedTimestamps(layerId, timestamps);
  return timestamps;
}
